import { Spacing } from '@persona-id/design-tokens';
import { CircleHelpIcon, PlusIcon } from '@persona-id/design-tokens/icons';
import styled, { css } from 'styled-components';

import { SectionDescription } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/shared';
import ContextMenu from 'dashboard/components/ContextMenu';
import HelpIcon from 'dashboard/components-lib/HelpIcon';
import TableLayout, {
  TableNullState,
  TableTD,
  TableTH,
  TableTR,
} from 'dashboard/layouts/TableLayout';
import { DisabledFeatureConfig } from 'dashboard/models/verification-template-version';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import Spacer from 'design-system/components/Spacer';
import Sticker from 'design-system/components/Sticker';
import { Label, Paragraph } from 'design-system/components/Text';
import Tooltip from 'design-system/components/Tooltip';

const StyledTableTR = styled(TableTR)<{ active?: boolean }>`
  ${({ active }) =>
    active &&
    css`
      background-color: ${({ theme }) => theme.color.variants.primary.highlightLight};
    `}
`;

const StickerContainer = styled.span`
  display: flex;
  gap: ${Spacing[1]};
`;

// TODO(lucas): Migrate to PDS component for underline tooltip indicators when available
const TooltipIndicatorWrapper = styled.div`
  width: fit-content;
  text-decoration: underline;
  text-decoration-style: dotted;
`;

interface DisabledFeatureConfigsTableProps {
  disabledFeatureConfigs: DisabledFeatureConfig[];
  selected: DisabledFeatureConfig | null;
  onSelect: (disabledFeatureConfig?: DisabledFeatureConfig | null) => void;
  onDelete: (disabledFeatureConfig: DisabledFeatureConfig) => void;
  editable: boolean;
}

const DisabledFeatureConfigsTable = ({
  disabledFeatureConfigs,
  selected,
  onSelect,
  onDelete,
  editable,
}: DisabledFeatureConfigsTableProps) => {
  const filteredDisabledFeatureConfigs = disabledFeatureConfigs.map(
    (disabledFeatureConfig, index) => [disabledFeatureConfig, index] as const,
  );

  const handleDelete = (disabledFeatureConfig: DisabledFeatureConfig) => {
    onDelete(disabledFeatureConfig);
    onSelect(undefined);
  };

  return (
    <Card>
      <Card.Body>
        <Card.Title
          actions={
            <Button
              id='disabled-feature-config-card__add-button'
              variant='basic'
              size='sm'
              onClick={() =>
                onSelect({
                  'country-code': null,
                  subdivision: null,
                  'id-classes': null,
                  'max-age-to-disable': undefined,
                  'disabled-features': [],
                })
              }
              disabled={!editable}
            >
              <PlusIcon />
            </Button>
          }
        >
          {editable ? (
            'Enhanced feature configuration'
          ) : (
            <Tooltip
              side='right'
              trigger={
                <TooltipIndicatorWrapper>Enhanced feature configuration</TooltipIndicatorWrapper>
              }
            >
              <Paragraph>Reach out to Persona support if you have questions.</Paragraph>
            </Tooltip>
          )}
        </Card.Title>
        <SectionDescription>
          Enhanced Configurations allow features to be selectively enabled or disabled by
          jurisdiction. A submitted Government ID is matched against the list of configurations
          below, from top to bottom.
        </SectionDescription>
        <Spacer height={0.5} />
        {disabledFeatureConfigs.length === 0 ? (
          <Paragraph>No custom enhanced configurations. Defaults are used.</Paragraph>
        ) : (
          <TableLayout
            showHeader
            aria-role='treegrid'
            isEmpty={filteredDisabledFeatureConfigs?.length === 0}
            containerMaxHeight={512}
            nullState={
              <TableNullState hasUsedFilters={false}>No enhanced configurations</TableNullState>
            }
            headerTR={
              <>
                <TableTH isSticky>
                  <Label size='md'>Country</Label>
                </TableTH>
                <TableTH isSticky>
                  <Label size='md'>Subdivision</Label>
                </TableTH>
                <TableTH isSticky>
                  <Label size='md'>ID Classes</Label>
                </TableTH>
                <TableTH isSticky>
                  <Label size='md'>Disabled Features</Label>
                </TableTH>
                <TableTH isSticky width='min' />
              </>
            }
          >
            {filteredDisabledFeatureConfigs.map(([disabledFeatureConfig]) => {
              const country = disabledFeatureConfig['country-code'];
              const subdivision = disabledFeatureConfig.subdivision;
              const idClasses = disabledFeatureConfig['id-classes'];
              const disabledFeatures = disabledFeatureConfig['disabled-features'];

              return (
                <StyledTableTR
                  key={`disabled-feature-config-row__${country}-${subdivision}`}
                  id={`disabled-feature-config-row__${country}-${subdivision}`}
                  onClick={editable ? () => onSelect(disabledFeatureConfig) : undefined}
                  active={disabledFeatureConfig === selected}
                >
                  <TableTD>{country === 'default' ? 'Any' : country}</TableTD>
                  <TableTD>
                    {subdivision === 'default' ? (
                      'Any'
                    ) : subdivision === 'QC' ? (
                      <>
                        {subdivision}
                        <HelpIcon icon={<CircleHelpIcon />}>
                          By default, biometrics is disabled in this jurisdiction. Please ensure you
                          are compliant with local regulations before you enable biometrics
                          processing and/or storage
                        </HelpIcon>
                      </>
                    ) : (
                      subdivision
                    )}
                  </TableTD>
                  <TableTD>
                    {typeof idClasses === 'string' || idClasses == null
                      ? 'Any'
                      : idClasses.join(', ')}
                  </TableTD>
                  <TableTD>
                    <StickerContainer
                      key={`disabled-feature-config-row__${country}-${subdivision}__disabled-features`}
                    >
                      {disabledFeatures.map((item) => (
                        <Sticker key={item}>{item}</Sticker>
                      ))}
                    </StickerContainer>
                  </TableTD>
                  <TableTD>
                    <ContextMenu
                      variant='inverse'
                      disabled={!editable}
                      items={[
                        {
                          id: 'disabled-feature-config-row__context-menu-delete',
                          label: 'Delete',
                          onClick: () => handleDelete(disabledFeatureConfig),
                          variant: 'danger',
                        },
                      ]}
                      renderItem={(item) => (
                        <ContextMenu.Item {...item}>{item.label}</ContextMenu.Item>
                      )}
                    />
                  </TableTD>
                </StyledTableTR>
              );
            })}
          </TableLayout>
        )}
      </Card.Body>
    </Card>
  );
};

export default DisabledFeatureConfigsTable;
